// libraries
import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Stack, Typography, Tooltip } from '@mui/material'
import { Save as SaveIcon, AddCircle as AddCircleIcon, Lock as LockIcon } from '@mui/icons-material'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { useTheme } from '@mui/material/styles'
import Dinero from 'dinero.js'

// state
import { setSnackBar, showFormModal } from 'state/ui'
import { clearDuplicatePurchaseOrderId, clearIsSetup, setIsSetup } from 'state/purchaseOrder'
import { clearCustomer } from 'state/customer'

// specific components
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import {
  getDuplicateOverrideNameError,
  getOverrideNameTransferError,
  initialPurchaseOrderInformation,
} from 'components/containers/contracts/utils'
import SubFormLayout from 'components/layouts/SubFormLayout'
import ContractMaterialsList from 'components/containers/contracts/ContractMaterialsList'

// generic components
import RouterPrompt from 'components/generic/modals/router-prompt/RouterPrompt'
import Input from 'components/generic/form/Input'
import { SelectChip } from 'components/generic/chip'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import FileUploader from 'components/generic/file-uploader/FileUploader'
import NewPaymentTermModal from 'components/generic/modals/dialog-modal/NewPaymentTermModal'

// helpers & utils
import { formOptions } from 'utils/validation/purchaseOrder'
import { trackEvent } from 'utils/tracking'
import { calculateTotalValue, addToDate } from 'utils/data-manipulation/common'
import { checkIfDateIsLocked, generateLockTooltip } from 'utils/helpers'
import {
  PURCHASE_ORDER_DROPDOWN_VALUES,
  ORDER_STATUSES,
  PRICE_SHEET_TYPES,
  CHURN_ZERO_EVENTS as cz,
  UNIT_IDS,
  CUSTOMERS_FILTERS,
} from 'utils/constants'
import { useCreatePriceSheetMutation, useUpdatePriceSheetMutation, useGetPriceSheetQuery } from 'services/priceSheets'
import {
  useGetPurchaseOrderQuery,
  useCreatePurchaseOrderMutation,
  useUpdatePurchaseOrderMutation,
  useArchivePurchaseOrderMutation,
  useGetPurchaseOrdersQuery,
} from 'services/purchaseOrders'
import { useGetMaterialTypesQuery } from 'services/materialTypes'
import { useGetCustomerQuery } from 'services/customers'
import useGetPricePrepend from 'utils/custom-hooks/useGetPricePrepend'
import FORM_MODALS from 'utils/ui/modals'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import usePaymentTerms from 'utils/custom-hooks/usePaymentTerms'
import useIsTransfer from 'utils/custom-hooks/useIsTransfer'

const { PACKAGE_OPTIONS, PICKUP_OPTIONS, ALL_PAYMENT_METHOD_OPTIONS, PAYMENT_OPTIONS_OPTIONS, CURRENCY_OPTIONS } =
  PURCHASE_ORDER_DROPDOWN_VALUES

const EditablePurchaseOrderContainer = () => {
  const contentContext = useContext(ContentLayoutContext)
  const tabsContext = useContext(TabsLayoutContext)
  const theme = useTheme()
  const dispatch = useDispatch()

  const { customer: supplier } = useSelector(state => state.customer)
  const { duplicatePurchaseOrderId: duplicatePurchaseOrderIdRedux, isSetup } = useSelector(state => state.purchaseOrder)
  const { paymentTermsOptions } = usePaymentTerms({ getOnlyActive: true })
  const [duplicatePurchaseOrderId, setDuplicatePurchaseOrderId] = useState(null)
  const [openPaymentTermModal, setOpenPaymentTermModal] = useState(false)
  const { hasAtm, details, defaultCurrency, defaultYardUm, settings } = useSelector(state => state.settings)
  const yardHasGsPayConfigured = details.gsPayEnabled
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}
  const yardPreferredPaymentMethod = settings?.purchaseSettings?.payment?.yardPreferredPaymentMethod

  useEffect(() => {
    if (duplicatePurchaseOrderIdRedux) {
      setDuplicatePurchaseOrderId(duplicatePurchaseOrderIdRedux)
    }
  }, [duplicatePurchaseOrderIdRedux])

  useEffect(() => {
    if (duplicatePurchaseOrderId) {
      dispatch(clearDuplicatePurchaseOrderId())
    }
  }, [duplicatePurchaseOrderId])
  const { id: paramsPurchaseOrderId } = useParams()

  const purchaseOrderId = useMemo(
    () => duplicatePurchaseOrderId || paramsPurchaseOrderId,
    [duplicatePurchaseOrderId, paramsPurchaseOrderId],
  )

  const isUpdate = !duplicatePurchaseOrderId && !!purchaseOrderId

  const { canUploadFilesPO, canCustomizeOrderNumbers, enableTransferTicketsConsumerWeightsFF, enableLockAccountingV2 } =
    useSelector(state => state.featureFlags?.variables || {})

  const { isAdmin, permissions } = useSelector(state => state.permissions)
  const hasPermissions = isAdmin || permissions.indexOf(isUpdate ? 'PriceSheetUpdate' : 'PriceSheetCreate') > -1

  const materialsApiRef = useGridApiRef()

  const PAYMENT_METHOD_OPTIONS = useMemo(
    () =>
      ALL_PAYMENT_METHOD_OPTIONS?.filter(
        method => (hasAtm ? true : method.id !== 'atm') && (yardHasGsPayConfigured ? true : method.id !== 'gs_pay'),
      ),
    [hasAtm, yardHasGsPayConfigured],
  )

  const navigate = useNavigate()
  const methods = useForm(formOptions)
  const { setError, formState, clearErrors, watch, register, setValue, getValues, handleSubmit, control, reset } =
    methods
  const { dirtyFields, isValid } = formState
  const isDirty = !!Object.keys(dirtyFields).length
  register('uploadcareGroupId', { value: '' })
  const [availableLocations, setAvailableLocations] = useState([])
  const [initialMaterials, setInitialMaterials] = useState([])
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [hasEditedMaterials, setHasEditedMaterials] = useState(false)
  const [readableId, setReadableId] = useState('')
  const [selectedSupplierId, setSelectedSupplierId] = useState('')
  const [purchaseOrderIDList, setPurchaseOrderIDList] = useState([])
  const [isPONumberValid, setIsPONumberValid] = useState(true)

  const [createPriceSheet, { isLoading: isLoadingCreatingPriceSheet }] = useCreatePriceSheetMutation()

  const [updatePriceSheet, { isLoading: isLoadingUpdatingPriceSheet }] = useUpdatePriceSheetMutation()

  const { data: purchaseOrderData, isLoading: isLoadingPurchaseOrder } = useGetPurchaseOrderQuery(
    { purchaseOrderId },
    { skip: !isUpdate && !duplicatePurchaseOrderId },
  )

  const { data: purchaseOrdersData, isLoading: isLoadingPurchaseOrdersData } = useGetPurchaseOrdersQuery({})

  const { data: priceSheetData, isLoading: isLoadingPriceSheetData } = useGetPriceSheetQuery(
    { priceSheetId: purchaseOrderData?.priceSheetId },
    { skip: !purchaseOrderData?.priceSheetId },
  )

  // prefetch initial material dropdown results (GREEN-8185)
  useGetMaterialTypesQuery({ filter: JSON.stringify({ search: '', priceSheetId: '' }), pageSize: 50 })

  const resetObjectEntries = objectEntries =>
    objectEntries.map(material => ({
      ...material,
      buyPrice: {
        price: {
          ...material?.buyPrice?.price,
          amount: 0,
        },
      },
    }))

  const { data: customerData, isLoading: isLoadingCustomerData } = useGetCustomerQuery(
    { customerId: selectedSupplierId },
    { refetchOnMountOrArgChange: true, skip: !selectedSupplierId },
  )

  const isTransfer = useIsTransfer(selectedSupplierId, customerData)

  const [createPurchaseOrder, { isLoading: isLoadingCreatingPurchaseOrder }] = useCreatePurchaseOrderMutation()

  const [updatePurchaseOrder, { isLoading: isLoadingUpdatePurchaseOrder }] = useUpdatePurchaseOrderMutation()

  const [archivePurchaseOrder, { isLoading: isLoadingArchivePurchaseOrder }] = useArchivePurchaseOrderMutation()

  const orderCurrency = watch('currency')
  const { pricePrepend } = useGetPricePrepend({ selectedCurrency: orderCurrency })

  const isInLockedAccountingPeriodV2 = useMemo(() => {
    if (!enableLockAccountingV2) return false
    if (!purchaseOrderData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false

    const effectiveDateIsLocked = checkIfDateIsLocked(lockAccountingPeriod, purchaseOrderData?.order?.effectiveDate)
    const expirationDateIsLocked = checkIfDateIsLocked(lockAccountingPeriod, purchaseOrderData?.order?.expirationDate)
    return effectiveDateIsLocked || expirationDateIsLocked
  }, [lockAccountingPeriod, purchaseOrderData, enableLockAccountingV2])

  useEffect(() => {
    // redirect to static page if PO is archived
    if (purchaseOrderData?.archivedAt) {
      navigate(`/app/contracts/purchase-orders/${paramsPurchaseOrderId}`)
    }
  }, [paramsPurchaseOrderId, navigate, purchaseOrderData])

  useEffect(() => {
    // skip if selectedSupplierId doesn't match customerData
    if (selectedSupplierId && customerData?.id !== selectedSupplierId) return

    const supplierLocations = selectedSupplierId
      ? [...(customerData?.locations || [])].sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
      : []
    setAvailableLocations(supplierLocations)

    // if supplier field hasn't changed, use data from purchaseOrderData
    if (!dirtyFields?.supplier) return

    const defaultCorporateLocation = supplierLocations.find(location => !!location.isDefaultCorporateLocation)
    setValue('purchaseOrderLocationId', defaultCorporateLocation?.id || supplierLocations?.[0]?.id || '')
    setValue('paymentTermId', selectedSupplierId ? customerData?.defaultPaymentTermId || '' : '')
    setValue(
      'paymentMethod',
      selectedSupplierId ? customerData?.preferredPaymentMethod?.toLowerCase() || yardPreferredPaymentMethod || '' : '',
    )
    setValue('currency', selectedSupplierId ? customerData?.defaultCurrency || defaultCurrency : defaultCurrency)
  }, [customerData, selectedSupplierId])

  const getMaterials = useCallback(
    () => Array.from(materialsApiRef.current.getRowModels().values()).slice(0, -1),
    [materialsApiRef],
  )

  const isLoading =
    isLoadingPriceSheetData ||
    isLoadingPurchaseOrder ||
    isLoadingCustomerData ||
    isLoadingCreatingPriceSheet ||
    isLoadingUpdatingPriceSheet ||
    isLoadingCreatingPurchaseOrder ||
    isLoadingUpdatePurchaseOrder ||
    isLoadingArchivePurchaseOrder ||
    isLoadingPurchaseOrdersData ||
    false

  useGlobalLoader(isLoading)

  const handleSuccess = (id, type) => {
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: `Purchase Order #${id} ${type}`,
      }),
    )
  }

  const handleError = (error, type) => {
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: error?.data?.error || `There was an error ${type} your purchase order`,
      }),
    )
  }

  const formatPurchaseOrderData = (data, priceSheet) => {
    const { supplier: supplierData, order } = data
    let formattedPO = {
      customerSONumber: data.customerSONumber,
      fulfilledAmount: isUpdate ? data.fulfilledAmount : 0,
      status: isUpdate ? data.status : 'Open',
      notes: data.notes,
      priceSheetId: priceSheet.id,
      order: {
        effectiveDate: Date.parse(order.effectiveDate),
        expirationDate: Date.parse(order.expirationDate),
        packageType: order.packageType,
        pickupDelivery: order.pickupDelivery,
        receivingYard: order.receivingYard,
      },
      paymentMethod: data.paymentMethod,
      paymentOptions: data.paymentOptions,
      paymentTermId: data.paymentTermId || null,
      currency: data?.currency || UNIT_IDS.USD,
      purchaseOrderLocationId: data.purchaseOrderLocationId,
      supplierId: supplierData.id,
      uploadcareGroupId: data?.uploadcareGroupId,
      docs: data.docs,
    }
    if (isUpdate) {
      formattedPO = {
        ...formattedPO,
        id: data.id,
        order: {
          ...formattedPO.order,
          id: data.orderId,
        },
      }
    }

    if (canCustomizeOrderNumbers && data?.purchaseOrderTicketId) {
      formattedPO.purchaseOrderTicketId = data.purchaseOrderTicketId
    }

    return formattedPO
  }

  const formatMaterialData = materials =>
    materials.map(material => {
      const { price = 0 } = material
      return {
        id: material.id,
        materialCode: material.code,
        materialTypeId: material.materialTypeId,
        priceSheetId: purchaseOrderData?.priceSheetId,
        description: material.description,
        overrideName: material.overrideName?.trim(),
        targetWeight: material.targetWeight,
        weightUnits: material.um,
        isNew: false,
        buyPrice: {
          id: material.priceId,
          isBuyPrice: true,
          priceType: 'SPOT',
          price: {
            amount: price * 10000,
            currency: getValues('currency'),
            precision: 4,
          },
        },
      }
    })

  const onSubmit = async data => {
    if (!hasPermissions) {
      handleError({
        data: {
          error:
            'You do not have sufficient privileges to make this change. Please reach out to your administrator if you need to update your permissions',
        },
      })
      return
    }

    const formattedMaterialData = formatMaterialData(getMaterials()).filter(material => !!material.materialCode)

    if (formattedMaterialData.length === 0) {
      handleError({
        data: {
          error: 'Purchase orders need to contain at least 1 material',
        },
      })
      return
    }

    if (enableTransferTicketsConsumerWeightsFF) {
      const overrideNameTransferError = getOverrideNameTransferError(isTransfer, formattedMaterialData)
      if (overrideNameTransferError) {
        handleError(overrideNameTransferError)
        return
      }
    }

    const overrideNameError = getDuplicateOverrideNameError(formattedMaterialData)
    if (overrideNameError) {
      handleError(overrideNameError)
      return
    }

    if (isUpdate) {
      const { data: priceSheet } = await updatePriceSheet({
        priceSheetId: purchaseOrderData.priceSheetId,
        priceSheet: {
          baseName: PRICE_SHEET_TYPES.PURCHASE_ORDER,
          id: purchaseOrderData.priceSheetId,
          entriesObj: formattedMaterialData,
          type: PRICE_SHEET_TYPES.PURCHASE_ORDER,
        },
      })
      const formattedData = formatPurchaseOrderData(data, priceSheet)
      await updatePurchaseOrder({
        purchaseOrderId: formattedData.id,
        purchaseOrder: formattedData,
      })
        .unwrap()
        .then(updatedPurchaseOrder => {
          handleSuccess(updatedPurchaseOrder.purchaseOrderTicketId, 'updated')
          reset(data)
          setHasEditedMaterials(false)
          const ordersList = updatedPurchaseOrder?.priceSheet?.entriesObj
          trackEvent({
            eventName: cz.UPDATE_PURCHASE_ORDER,
            description: `${ordersList.length} Purchase Order${ordersList.length === 1 ? '' : 's'} updated`,
            quantity: calculateTotalValue(ordersList, { pricePath: 'buyPrice.price' }),
          })
          navigate(`/app/contracts/purchase-orders/${formattedData.id}`)
        })
        .catch(errorMsg => {
          // placeholder for when BE validation is ready,
          // if (errorMsg === 'DUPE_CODE') {
          //   setError('purchaseOrderTicketId', { type: 'custom', message: `This number has been used` })
          // }
          trackEvent({ eventName: cz.UPDATE_PURCHASE_ORDER, errorMsg })
          handleError(errorMsg, 'updating')
        })
    } else {
      const { data: priceSheet } = await createPriceSheet({
        priceSheet: {
          baseName: PRICE_SHEET_TYPES.PURCHASE_ORDER,
          entriesObj: formattedMaterialData,
          type: PRICE_SHEET_TYPES.PURCHASE_ORDER,
        },
      })
      const formattedData = formatPurchaseOrderData(data, priceSheet)
      await createPurchaseOrder({ purchaseOrderCreation: formattedData })
        .unwrap()
        .then(newPurchaseOrder => {
          handleSuccess(newPurchaseOrder.purchaseOrderTicketId, 'created')
          reset(data)
          setHasEditedMaterials(false)
          const ordersList = newPurchaseOrder?.priceSheet?.entriesObj
          trackEvent({
            eventName: cz.CREATE_PURCHASE_ORDER,
            description: `${ordersList.length} Purchase Order${ordersList.length === 1 ? '' : 's'} created`,
            quantity: calculateTotalValue(ordersList, { pricePath: 'buyPrice.price' }),
          })
          navigate('/app/contracts/purchase-orders')
        })
        .catch(errorMsg => {
          // placeholder for when BE validation is ready,
          // if (errorMsg === 'DUPE_CODE') {
          //   setError('purchaseOrderTicketId', { type: 'custom', message: `This number has been used` })
          // }
          trackEvent({ eventName: cz.CREATE_PURCHASE_ORDER, errorMsg })
          handleError(errorMsg, 'creating')
        })
    }
  }

  const onClickArchive = async () => {
    setShowArchiveModal(false)

    await archivePurchaseOrder({
      purchaseOrderId,
    })
      .unwrap()
      .then(() => {
        handleSuccess(readableId, 'archived')
        navigate('/app/contracts/purchase-orders')
      })
      .catch(error => handleError(error, 'archiving'))
  }

  useEffect(() => {
    if (purchaseOrderData) {
      setReadableId(purchaseOrderData.purchaseOrderTicketId)
      setSelectedSupplierId(purchaseOrderData?.supplierId)
      const resetPurchaseOrder = {
        ...initialPurchaseOrderInformation,
        currency: defaultCurrency,
        ...purchaseOrderData,
        ...(duplicatePurchaseOrderId && {
          purchaseOrderTicketId: '',
          order: {
            ...purchaseOrderData.order,
            effectiveDate: new Date(),
            expirationDate: new Date(),
          },
        }),
      }
      reset(resetPurchaseOrder)
      register('docs', { value: purchaseOrderData.docs })
      contentContext.setContainerTitle(
        <Stack direction="row" alignItems="center" spacing={1}>
          {isInLockedAccountingPeriodV2 && (
            <Tooltip title={generateLockTooltip('purchase order', false)} placement="top">
              <LockIcon color="info" />
            </Tooltip>
          )}
          <Typography variant="h5" sx={{ flex: 1, pr: 2 }}>
            {duplicatePurchaseOrderId
              ? 'New Purchase Order'
              : `Purchase Order #${purchaseOrderData?.purchaseOrderTicketId}`}
          </Typography>
          <SelectChip control={control} items={ORDER_STATUSES} />
        </Stack>,
      )
    } else {
      contentContext.setContainerTitle('New Purchase Order')
    }
  }, [isInLockedAccountingPeriodV2, purchaseOrderData])

  useEffect(() => {
    tabsContext.setContainerTabsVisibility(false)

    const newPurchaseOrderActions = [
      {
        text: 'Create Purchase Order',
        disabled: (!isDirty && !hasEditedMaterials) || !isPONumberValid,
        id: 'create-purchase-order-button',
        onClick: handleSubmit(onSubmit),
      },
      // { // feature not complete yet
      //   text: 'Save As Draft',
      //   variant: 'outlined',
      //   disabled: (!isDirty && !hasEditedMaterials) || !isPONumberValid,
      //   id: 'save-as-draft-button',
      //   onClick: handleSubmit(onSubmitDraft),
      // },
      {
        text: 'Cancel',
        variant: 'text',
        id: 'cancel-button',
        onClick: () => {
          navigate('/app/contracts/purchase-orders')
        },
      },
    ]

    const editPurchaseOrderActions = [
      {
        text: 'Save Changes',
        variant: 'contained',
        icon: <SaveIcon />,
        disabled: !isDirty && !hasEditedMaterials,
        id: 'save-changes-button',
        onClick: handleSubmit(onSubmit),
      },
      {
        text: 'Cancel',
        variant: 'outlined',
        id: 'cancel-button',
        onClick: () => {
          navigate(`/app/contracts/purchase-orders/${purchaseOrderId}`)
        },
      },
      {
        text: 'Archive',
        variant: 'text',
        sx: { color: theme.palette.error.main },
        id: 'archive-button',
        onClick: () => {
          setShowArchiveModal(true)
        },
      },
    ]

    if (isUpdate) {
      contentContext.setContainerActions(editPurchaseOrderActions)
    } else {
      contentContext.setContainerActions(newPurchaseOrderActions)
    }
  }, [isDirty, hasEditedMaterials, isPONumberValid])

  const formatInitialMaterialData = (materials = []) =>
    materials?.map(material => ({
      id: material.id,
      priceId: material.buyPrice?.id,
      materialTypeId: material.materialTypeId,
      um: material.weightUnits || defaultYardUm,
      targetWeight: material.targetWeight,
      price: Dinero({ ...material.buyPrice?.price }).toUnit(),
      materialName: material.materialName,
      description: material.description,
      overrideName: material.overrideName,
      code: material.materialCode,
    }))

  useEffect(() => {
    if (priceSheetData) {
      const materialsToFormat = duplicatePurchaseOrderId
        ? resetObjectEntries(priceSheetData?.entriesObj)
        : priceSheetData?.entriesObj
      setInitialMaterials(formatInitialMaterialData(materialsToFormat))
    }
  }, [priceSheetData, duplicatePurchaseOrderId])

  const updateSupplierInfo = customer => {
    setSelectedSupplierId(customer?.id)
  }

  useEffect(() => {
    if (!isSetup) {
      dispatch(clearCustomer())
      dispatch(setIsSetup(true))
    }
    return () => dispatch(clearIsSetup())
  }, [])

  useEffect(() => {
    if (isSetup && supplier?.id) {
      const supplierValue =
        isUpdate || duplicatePurchaseOrderId
          ? { ...supplier, displayName: supplier.customerCommonIdentifierString }
          : supplier
      setValue('supplier', supplierValue)
      updateSupplierInfo(supplierValue)
      dispatch(clearCustomer())
    }
  }, [supplier])

  // temp FE validation until BE is done
  useEffect(() => {
    if (purchaseOrdersData && !purchaseOrderData) {
      const poIdList = purchaseOrdersData.map(po => po.purchaseOrderTicketId).sort((a, b) => a - b)
      setPurchaseOrderIDList(poIdList)
    }
  }, [purchaseOrdersData, purchaseOrderData])

  const checkIfPOUsed = ({ target: { value } }) => {
    if (purchaseOrderIDList.includes(parseInt(value, 10))) {
      setError('purchaseOrderTicketId', { type: 'custom', message: 'This number has been used' })
      setIsPONumberValid(false)
    }
  }

  const onUploadFiles = useCallback(uploadUUID => {
    setValue('uploadcareGroupId', uploadUUID)
    onSubmit(getValues())
  }, [])

  const handleRemoveFiles = files => {
    setValue('docs', files)
    onSubmit(getValues())
  }

  return (
    <>
      <AlertModal
        open={showArchiveModal}
        modalType="void"
        title="Archive This Purchase Order"
        question={`Are you sure you want to archive Purchase Order #${purchaseOrderData?.purchaseOrderTicketId}?`}
        description="Archiving is irreversible and you will need to contact GreenSpark support to undo this action."
        confirmLabel="YES, CONFIRM ARCHIVE"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowArchiveModal(false)
        }}
        onSubmit={onClickArchive}
      />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <RouterPrompt
            title="Cancel Changes to This Purchase Order"
            question="Are you sure you want to cancel changes to this purchase order?"
            description="Changes to the purchase order will be cancelled and all unsaved data on the purchase order will be lost."
            when={isDirty || hasEditedMaterials}
          />
          <NewPaymentTermModal
            open={openPaymentTermModal}
            onSave={newItemId => {
              setValue('paymentTermId', newItemId, { shouldDirty: true })
              setOpenPaymentTermModal(false)
            }}
            onClose={() => setOpenPaymentTermModal(false)}
          />

          <Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} p={3} spacing={3}>
              <SubFormLayout
                formName="Supplier Information"
                fields={[
                  [
                    {
                      name: 'supplier',
                      label: 'Supplier Name',
                      dataTest: 'supplier',
                      fieldType: 'customerAutocomplete',
                      defaultValue: {},
                      baseFilter: CUSTOMERS_FILTERS.SUPPLIER,
                      labelKey: isUpdate || duplicatePurchaseOrderId ? 'displayName' : 'customerCommonIdentifierString',
                      onChange: updateSupplierInfo,
                      sideButton: {
                        variant: 'text',
                        startIcon: <AddCircleIcon />,
                        onClick: () =>
                          dispatch(
                            showFormModal({
                              formModalName: FORM_MODALS.NEW_CUSTOMER,
                              formModalProps: { type: 'purchaseOrder' },
                            }),
                          ),
                      },
                    },
                    {
                      name: 'customerSONumber',
                      label: 'Customer SO#',
                      dataTest: 'customer-so-number',
                      fieldType: 'input',
                    },
                  ],
                  [
                    {
                      name: 'purchaseOrderLocationId',
                      label: 'Location',
                      dataTest: 'purchase-order-location-id',
                      fieldType: 'dropdown',
                      dropdownOptions: availableLocations,
                    },
                  ],
                ]}
              />
              <SubFormLayout
                formName="Order Details"
                fields={[
                  [
                    {
                      name: 'purchaseOrderTicketId',
                      label: 'Custom PO #',
                      dataTest: 'order-po-number',
                      fieldType: 'inputTooltip',
                      onBlur: e => {
                        checkIfPOUsed(e)
                      },
                      disabled: !!purchaseOrderId && !duplicatePurchaseOrderId,
                      tooltip: 'An anto-generated number will be applied if this field is left blank',
                      placement: 'top',
                      onChange: () => {
                        clearErrors('purchaseOrderTicketId')
                        setIsPONumberValid(true)
                      },
                      hidden: isUpdate,
                      InputProps: {
                        type: 'number',
                        decimalscale: 0,
                        inputProps: { min: 0 },
                      },
                    },
                  ],
                  [
                    {
                      name: 'order.effectiveDate',
                      label: 'Effective Date',
                      dataTest: 'order-effective-date',
                      fieldType: 'date',
                      defaultDate: new Date(),
                      datePickerProps: {
                        minDate:
                          enableLockAccountingV2 && lockAccountingPeriod.isLockedForAccounting
                            ? addToDate(new Date(lockAccountingPeriod.lockAccountingDate))
                            : undefined,
                      },
                    },
                    {
                      name: 'order.expirationDate',
                      label: 'Expiration Date',
                      dataTest: 'order-expiration-date',
                      fieldType: 'date',
                      defaultDate: new Date(),
                      datePickerProps: {
                        minDate:
                          enableLockAccountingV2 && lockAccountingPeriod.isLockedForAccounting
                            ? addToDate(new Date(lockAccountingPeriod.lockAccountingDate))
                            : undefined,
                      },
                    },
                  ],
                  [
                    {
                      name: 'order.packageType',
                      label: 'Package Type',
                      dataTest: 'order-package-type',
                      fieldType: 'dropdown',
                      dropdownOptions: PACKAGE_OPTIONS,
                    },
                    {
                      name: 'order.pickupDelivery',
                      label: 'Pickup/Delivery?',
                      dataTest: 'order-pickup-delivery',
                      fieldType: 'dropdown',
                      dropdownOptions: PICKUP_OPTIONS,
                    },
                  ],
                  [
                    {
                      name: 'order.receivingYard',
                      label: 'Receiving Yard',
                      dataTest: 'order-receiving-yard',
                      fieldType: 'input',
                    },
                  ],
                ].slice(canCustomizeOrderNumbers ? 0 : 1)}
              />
              <SubFormLayout
                formName="Payment Info"
                fields={[
                  [
                    {
                      name: 'paymentTermId',
                      label: 'Payment Terms',
                      dataTest: 'payment-terms',
                      fieldType: 'dropdown',
                      dropdownOptions: paymentTermsOptions,
                      sideButton: {
                        variant: 'text',
                        startIcon: <AddCircleIcon />,
                        onClick: () => {
                          setOpenPaymentTermModal(true)
                        },
                      },
                    },
                    {
                      name: 'paymentMethod',
                      label: 'Payment Method',
                      dataTest: 'payment-method',
                      fieldType: 'dropdown',
                      dropdownOptions: PAYMENT_METHOD_OPTIONS,
                    },
                  ],
                  [
                    {
                      name: 'currency',
                      label: 'Currency',
                      dataTest: 'currency',
                      fieldType: 'dropdown',
                      dropdownOptions: CURRENCY_OPTIONS,
                      defaultValue: defaultCurrency,
                      disabled: true,
                    },
                    {
                      name: 'paymentOptions',
                      label: 'Payment Options',
                      dataTest: 'payment-options',
                      fieldType: 'dropdown',
                      dropdownOptions: PAYMENT_OPTIONS_OPTIONS,
                    },
                  ],
                ]}
              />
            </Stack>
          </Stack>
          <ContractMaterialsList
            materials={initialMaterials}
            apiRef={materialsApiRef}
            loading={isLoading}
            isUpdate={isUpdate}
            duplicateContractTypeOrderId={duplicatePurchaseOrderId}
            onEditCallback={() => setHasEditedMaterials(true)}
            control={control}
            pricePrepend={pricePrepend}
            isTransfer={isTransfer}
          />
          <Box p={3}>
            <Stack direction="row" mb={1}>
              <Typography variant="h6" gutterBottom color="textSecondary">
                Notes
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center">
              <Input name="notes" defaultValue="" control={control} fullWidth multiline />
              {canUploadFilesPO && (
                <div style={{ marginLeft: '1.5em' }}>
                  <FileUploader
                    filesData={purchaseOrderData?.docs}
                    disabled={!isValid}
                    onUploadFiles={onUploadFiles}
                    onRemoveFiles={handleRemoveFiles}
                  />
                </div>
              )}
            </Stack>
          </Box>
        </form>
      </FormProvider>
    </>
  )
}

export default EditablePurchaseOrderContainer
